import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import './index.css'
import './styles/animations.css'
import App from './App.tsx'
import { ResumeProvider } from './context/ResumeContext.tsx'
import { RetailorJobProvider } from './context/RetailorJobContext.tsx'
import { AuthProvider } from './hooks/useAuth'
import { reportWebVitals } from './utils/webVitals'

async function enableMocking() {
  // Only enable mocks if VITE_ENABLE_MOCKS is set to 'true'
  if (import.meta.env.VITE_ENABLE_MOCKS !== 'true') {
    return
  }

  const { worker } = await import('./mocks/browser')

  // Start MSW worker with options
  return worker.start({
    onUnhandledRequest: 'bypass', // Don't warn about unhandled requests
    serviceWorker: {
      url: '/mockServiceWorker.js',
    },
  })
}

enableMocking().then(() => {
  const container = document.getElementById('root')!
  const tree = (
    <StrictMode>
      <AuthProvider>
        <ResumeProvider>
          <RetailorJobProvider>
            <App />
          </RetailorJobProvider>
        </ResumeProvider>
      </AuthProvider>
    </StrictMode>
  )

  // The landing page is rendered to HTML at build time (scripts/prerender.mjs),
  // so in production the markup is already on screen before this file runs.
  // Hydrating adopts those nodes; createRoot would throw them away and paint
  // again, which is a second largest-contentful-paint and the whole reason the
  // page measured 3.4s. The provider tree here must match entry-ssr.tsx.
  if (container.dataset.prerendered === '1') {
    hydrateRoot(container, tree)
  } else {
    createRoot(container).render(tree)
  }

  // Started after mount so it never delays the paint it is measuring.
  reportWebVitals()
})
